"use client"
// PC 首页「场景融合」区：多种"社交+游戏"场景深度融合——场景 TAB 弹簧 pill + 竖版短视频切片轮播（Embla + autoplay）
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import { BlurFade } from './BlurFade'
import HlsVideo from './HlsVideo'
import { cn } from '@/lib/utils'
import { homeDict, type HomeFusionDict, type HomeFusionClip } from '@/lib/i18n/dicts/home'
import type { LocalizedGame } from '@/lib/game-i18n'

// 中文文件名统一编码（video src/poster 为懒加载不走 SSR preload，此处仅作保险）
const enc = (u: string) => encodeURI(u)

export interface FusionSectionProps {
    dict?: HomeFusionDict
    games?: LocalizedGame[]
}

export default function FusionSection({ dict = homeDict.zh.fusion, games = [] }: FusionSectionProps) {
    const [active, setActive] = useState(0)
    const current = dict.tabs[active] ?? dict.tabs[0]

    // 游戏切片按 gameid 解析本地化游戏名；解析不到（或非游戏切片）回退字典 title
    const nameFor = (clip: HomeFusionClip) => {
        if (clip.gameid == null) return clip.title
        return games.find((g) => g.gameid === clip.gameid)?.name ?? clip.title
    }

    return (
        <section className="w-full bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto py-16 md:py-20 px-4 sm:px-6 lg:px-8">
                <BlurFade delay={0.05} inView>
                    <div className="text-center">
                        {/* 标题区与能力矩阵（Section41）同款：纯文本 kicker + 实心大标题 + 副标题 */}
                        <h2 className="text-base/7 font-semibold text-indigo-600">{dict.kicker}</h2>
                        <p className="mx-auto mt-2 max-w-3xl text-3xl font-semibold tracking-tight text-balance text-gray-950 sm:text-4xl">
                            {dict.heading}
                        </p>
                        <p className="mx-auto mt-4 max-w-2xl text-sm md:text-base text-gray-500">
                            {dict.sub}
                        </p>
                    </div>
                </BlurFade>

                {/* 场景 TAB 弹簧 pill（layoutId 共享位移动画，与 Section41/gamelist2 一致） */}
                <BlurFade delay={0.12} inView>
                    <div className="mt-10 flex flex-wrap justify-center gap-2">
                        {dict.tabs.map((tab, i) => (
                            <button
                                key={tab.name}
                                type="button"
                                onClick={() => setActive(i)}
                                className={cn(
                                    'relative rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300',
                                    active === i ? 'text-white' : 'text-slate-500 hover:text-slate-800'
                                )}
                            >
                                {active === i && (
                                    <motion.span
                                        layoutId="fusion-scene-pill"
                                        className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 shadow-lg shadow-indigo-600/30"
                                        transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                                    />
                                )}
                                <span className="relative z-10">{tab.name}</span>
                            </button>
                        ))}
                    </div>
                </BlurFade>

                {/* 切片轮播：TAB 切换时整组淡入 */}
                <div className="mt-10">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8, transition: { duration: 0.15 } }}
                            transition={{ duration: 0.3 }}
                        >
                            {current && <ClipCarousel clips={current.clips} nameFor={nameFor} />}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    )
}

interface ClipCarouselProps {
    clips: HomeFusionClip[]
    nameFor: (clip: HomeFusionClip) => string
}

// 单组切片轮播：Embla loop + 自动播放（悬停暂停、交互后不停止）
function ClipCarousel({ clips, nameFor }: ClipCarouselProps) {
    const autoplay = useMemo(
        () => Autoplay({ delay: 2600, stopOnInteraction: false, stopOnMouseEnter: true }),
        []
    )
    const [carouselRef] = useEmblaCarousel({ loop: true, align: 'start' }, [autoplay])

    // 每组切片仅 2 条，不足以铺满视口无法无缝 loop，重复填充至 ≥8 条
    const slides = useMemo(() => {
        if (clips.length === 0) return []
        const times = Math.max(1, Math.ceil(8 / clips.length))
        return Array.from({ length: times }, () => clips).flat()
    }, [clips])

    return (
        <div className="overflow-hidden" ref={carouselRef}>
            <div className="flex -ml-4 md:-ml-5">
                {slides.map((clip, i) => (
                    <div key={`${clip.video}-${i}`} className="min-w-0 shrink-0 grow-0 basis-full sm:basis-1/2 lg:basis-1/3 pl-4 md:pl-5">
                        <ClipCard clip={clip} name={nameFor(clip)} delayIndex={i % 3} />
                    </div>
                ))}
            </div>
        </div>
    )
}

interface ClipCardProps {
    clip: HomeFusionClip
    name: string
    /** 呼吸光环的错峰延迟档位（0/1/2），避免整排同频闪烁 */
    delayIndex?: number
}

// 竖版切片卡：手机样机框美化（黑色 bezel + 银色描边 + 灵动岛），屏幕区比例 560:752 与演示视频一致，
// 视频铺满屏幕无黑边不裁剪；底部渐变层显示名称；有 gameid 的整卡跳详情页
// 性能：视频仅在本卡进入视口时才播放（离屏即暂停），preload="none" + poster 占位，避免整组视频并发加载造成滑动卡顿
// 呼吸态：描边光环错峰缓慢脉冲（纯 CSS，不影响布局）
function ClipCard({ clip, name, delayIndex = 0 }: ClipCardProps) {
    const videoRef = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        const v = videoRef.current
        if (!v) return
        const ob = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    v.play().catch(() => { })
                } else {
                    v.pause()
                }
            },
            { threshold: 0.2 }
        )
        ob.observe(v)
        return () => ob.disconnect()
    }, [])

    const card = (
        <div className="group relative w-full transition-transform duration-300 hover:scale-105">
            {/* 手机外壳：bezel 用百分比内边距，随卡宽等比缩放 */}
            <div className="relative w-full rounded-[2.6rem] bg-slate-950 px-[3%] py-[2.4%] ring-1 ring-slate-300/50 shadow-xl shadow-slate-900/20 transition-shadow duration-300 group-hover:shadow-2xl group-hover:shadow-indigo-500/30">
                {/* 侧边按键装饰（电源键 + 音量键） */}
                <span aria-hidden className="absolute -right-[2px] top-[18%] h-[7%] w-[3px] rounded-full bg-slate-400/70" />
                <span aria-hidden className="absolute -left-[2px] top-[14%] h-[5%] w-[3px] rounded-full bg-slate-400/70" />
                <span aria-hidden className="absolute -left-[2px] top-[22%] h-[9%] w-[3px] rounded-full bg-slate-400/70" />
                {/* 屏幕：比例 560:752，视频铺满 */}
                <div className="relative w-full aspect-[560/752] overflow-hidden rounded-[2rem] bg-black">
                    <HlsVideo
                        ref={videoRef}
                        muted
                        loop
                        playsInline
                        preload="none"
                        poster={enc(clip.poster)}
                        src={enc(clip.video)}
                        className="h-full w-full object-cover"
                    />
                    {/* 灵动岛 */}
                    <span aria-hidden className="absolute left-1/2 top-[1.8%] h-[2.4%] w-[24%] -translate-x-1/2 rounded-full bg-slate-950/95" />
                    {/* 底部名称渐变层 */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent px-3 pb-2.5 pt-8">
                        <p className="text-sm font-bold text-white drop-shadow">{name}</p>
                    </div>
                </div>
            </div>
            {/* 呼吸光环（错峰） */}
            <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[2.6rem] ring-2 ring-indigo-400/60 animate-breathe-glow"
                style={{ animationDelay: `${delayIndex * 0.9}s` }} />
        </div>
    )
    if (clip.gameid == null) return card
    return (
        <a href={`../gamedetail2/${clip.gameid}`} className="block">
            {card}
        </a>
    )
}
